// src/components/block/liveStreamManagment/SubtitlesControl.tsx
//
// Bouton « Sous-titres » de la section Diffusion de la ControlBar (2026-10-15),
// à côté de « On air » et « Multi view » : allume la transcription du flux final
// par Google dans le microservice de la chaîne. Les spectateurs de Kleon.tv et
// de l'Apple TV choisissent ensuite d'afficher la langue parlée ou sa traduction
// (l'anglais, ou le français pour un direct en anglais).
//
// Le menu à côté du bouton choisit la langue PARLÉE ; la changer pendant que les
// sous-titres tournent repart sur la nouvelle langue (quelques secondes sans
// sous-titres). Autonome : relit lui-même l'état de la chaîne, rien à faire
// passer par LiveStreamManager.

import React, { useEffect, useState } from 'react';

import AnimatedWaitingIcon from '../../widget/animations/animatedWaitingIcon';
import {
  fetchSubtitles,
  updateSubtitles,
  SubtitlesState,
} from '../../../backendServices/broadcastingManagment/api_subtitles';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState } from '../../../store/generalReducer';
import { useStreamDeckControl } from '../../../dataTypes/liveStreamManagment/streamDeckBridge';

interface Props {
  broadcastingChannelId : number;
  /** Style commun des boutons de la ControlBar. */
  buttonStyle           : React.CSSProperties;
}

const CaptionsIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 16, height: 16, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path strokeLinecap="round" d="M10.5 10.2a2.4 2.4 0 1 0 0 3.6M17 10.2a2.4 2.4 0 1 0 0 3.6" />
  </svg>
);

const SubtitlesControl: React.FC<Props> = ({ broadcastingChannelId, buttonStyle }) => {
  const [state,   setState]   = useState<SubtitlesState | null>(null);
  const [busy,    setBusy]    = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [hover,   setHover]   = useState(false);

  // Option réservée aux profils payants (2026-10-19) : tant qu'aucune offre n'est
  // branchée sur la fiche créateur, seul le Superadmin peut allumer les sous-titres
  // (Google Speech-to-Text et traduction sont facturés à la minute).
  const { authentication } = useTypedSelector((s: RootState) => s.authenticationReducer);
  const locked = authentication.admin?.role !== 'ADMIN';

  useEffect(() => {
    if (locked) return;
    let cancelled = false;
    setState(null);
    fetchSubtitles(broadcastingChannelId)
      .then(next => { if (!cancelled) setState(next); })
      .catch(() => { if (!cancelled) setWarning('Réglage des sous-titres illisible'); });
    return () => { cancelled = true; };
  }, [broadcastingChannelId, locked]);

  const apply = (enabled: boolean, language?: string) => {
    if (!state) return;
    const previous = state;
    setBusy(true);
    setWarning(null);
    setState({ ...state, enabled, language: language ?? state.language });
    updateSubtitles(broadcastingChannelId, enabled, language)
      .then(next => {
        setState(next);
        if (next.applied === false) {
          setWarning("Réglage enregistré, mais le microservice de la chaîne n'a pas répondu");
        }
      })
      .catch(() => {
        setState(previous);
        setWarning("Les sous-titres n'ont pas pu être changés");
      })
      .finally(() => setBusy(false));
  };

  // Touche « Sous-titres » du Stream Deck : même bascule que le bouton.
  useStreamDeckControl('subtitles', {
    available: !locked && !!state && !busy,
    active:    state?.enabled ?? false,
    busy,
    detail:    state ? (state.languages.find(l => l.language === state.language)?.label ?? state.language) : null,
  }, cmd => {
    if (locked || !state || busy) return;
    const next = cmd.action === 'toggle' ? !state.enabled : cmd.action === 'start';
    if (next !== state.enabled) apply(next);
  });

  const enabled     = state?.enabled ?? false;
  const activating  = busy && enabled;
  const translation = state?.translation === 'fr' ? 'français' : 'anglais';
  const title = locked ? undefined : warning
    ?? (enabled
      ? `Sous-titres en direct : ${state?.languages.find(l => l.language === state.language)?.label ?? state?.language}, traduits en ${translation}. Les spectateurs les affichent depuis leur lecteur (environ 8 s de retard en plus).`
      : 'Sous-titrer le flux final en direct (Google Speech-to-Text) : les spectateurs de Kleon.tv et de l’Apple TV pourront les afficher.');

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}
      onMouseEnter={() => locked && setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {locked && hover && (
        <div role="tooltip" style={{
          position: 'absolute', bottom: 'calc(100% + 10px)', left: 0, zIndex: 50,
          width: 230, padding: '10px 12px', borderRadius: 10,
          background: '#1f2937', color: '#fff', fontSize: 12, lineHeight: 1.4,
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.15)',
          pointerEvents: 'none',
        }}>
          <strong style={{ display: 'block', marginBottom: 2 }}>Option non disponible</strong>
          Les sous-titres sont désactivés pour les profils non payants.
          <span style={{
            position: 'absolute', top: '100%', left: 24, width: 10, height: 10,
            background: '#1f2937', transform: 'translateY(-5px) rotate(45deg)',
            borderRight: '1px solid rgba(255,255,255,0.15)', borderBottom: '1px solid rgba(255,255,255,0.15)',
          }} />
        </div>
      )}
      <button
        onClick={() => apply(!enabled)}
        disabled={locked || !state || busy}
        title={title}
        style={{
          ...buttonStyle,
          background: (enabled || activating) ? '#dc2626' : buttonStyle.background as string,
          cursor: (locked || !state || busy) ? 'not-allowed' : 'pointer',
          opacity: locked ? 0.5 : buttonStyle.opacity,
          animation: busy ? (enabled ? 'liveGlowRed 1s ease-in-out infinite' : 'liveGlowGrey 1s ease-in-out infinite') : undefined,
        }}
      >
        {busy ? <AnimatedWaitingIcon /> : <CaptionsIcon />}
        Sous-titres
      </button>
      <select
        value={state?.language ?? 'fr-FR'}
        disabled={locked || !state || busy}
        onChange={e => apply(enabled, e.target.value)}
        title="Langue parlée pendant le direct"
        style={{
          ...buttonStyle,
          width: 92, padding: '0 6px',
          cursor: (locked || !state || busy) ? 'not-allowed' : 'pointer',
          opacity: locked ? 0.5 : buttonStyle.opacity,
        }}
      >
        {(state?.languages ?? [{ language: 'fr-FR', label: 'Français' }]).map(choice => (
          <option key={choice.language} value={choice.language}>{choice.label}</option>
        ))}
      </select>
    </div>
  );
};

export default SubtitlesControl;
